import type { ApplePayConfig } from '@recurly/recurly-js';
import { ComponentProps, Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import type WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import { useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import SectionDivider from '@alltrails/denali/components/SectionDivider';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type ProResponse from '../../types/ProResponse';
import type { CardElementChangeEvent } from '../../types/ReactRecurly';
import { CARD_VALIDATION_NAMES, TokenValidationErrors } from '../../types/RecurlyTokenization';
import TokenizedCreditCardForm from '../TokenizedCreditCardForm';
import ApplePayButtonWithSDK from './ApplePayButtonWithSDK';
import ErrorText from './ErrorText';
import GooglePayButton from './GooglePayButton';
import LoadingOverlay from './LoadingOverlay';
import styles from './PaymentForm.module.scss';
import PayPalButton from './PayPalButton';
import { payPalSupportedCurrencies } from './payPalSupportedCurrencies';

type Props = {
  applePayConfig: ApplePayConfig;
  billingCountry: CountryCode;
  browserName?: string;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  errors?: ProResponse['errors'];
  isApplePayEnabled?: boolean;
  isComplianceCheckboxEnabled?: boolean;
  isReiCheckout?: boolean;
  isUpgrade?: boolean;
  onApplePayToken: ComponentProps<typeof ApplePayButtonWithSDK>['onApplePayToken'];
  onGooglePayToken: ComponentProps<typeof GooglePayButton>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PayPalButton>['onPayPalToken'];
  plan: Plan;
  setErrors: Dispatch<SetStateAction<ProResponse['errors'] | undefined>>;
  onBillingCountryChange: (countryCode: CountryCode, arg1: any) => void;
  setClientValidationErrors: (inputName: CARD_VALIDATION_NAMES, value: string | CardElementChangeEvent) => void;
  validationErrors: TokenValidationErrors;
  purchaseTier: PurchaseTier;
  webProPurchaseLocation: WebProPurchaseLocation;
  validateCompliance: (paymentMethod: PaymentMethod) => boolean;
};

export default function PaymentForm({
  applePayConfig,
  billingCountry,
  browserName,
  coupon,
  currencyCode,
  isComplianceCheckboxEnabled = false,
  isApplePayEnabled = true,
  isReiCheckout,
  isUpgrade,
  onApplePayToken,
  onGooglePayToken,
  onPayPalToken,
  plan,
  errors,
  setErrors,
  onBillingCountryChange,
  validationErrors,
  setClientValidationErrors,
  purchaseTier,
  webProPurchaseLocation,
  validateCompliance
}: Props) {
  // this submitting state is used for non-creditcard services
  const [submitting, setSubmitting] = useState<boolean>(false);

  const searchParams = useClientNavigationSearchParams();
  const isAndroidNativeApp = searchParams.get('app_source') === 'android';

  const productType = purchaseTier === 'peak' ? 'peak' : 'plus';

  return (
    <>
      <div className={styles.grid}>
        {isApplePayEnabled && (
          <ApplePayButtonWithSDK
            applePayConfig={applePayConfig}
            currencyCode={currencyCode}
            onApplePayToken={onApplePayToken}
            productType={productType}
            setErrors={setErrors}
            setSubmitting={setSubmitting}
            webProPurchaseLocation={webProPurchaseLocation}
            validateCompliance={validateCompliance}
          />
        )}
        <GooglePayButton
          coupon={coupon}
          currencyCode={currencyCode}
          onGooglePayToken={onGooglePayToken}
          plan={plan}
          productType={productType}
          setErrors={setErrors}
          setSubmitting={setSubmitting}
          webProPurchaseLocation={webProPurchaseLocation}
          validateCompliance={validateCompliance}
        />
        {payPalSupportedCurrencies.includes(currencyCode) && browserName !== 'WebKit' && !isAndroidNativeApp && (
          <PayPalButton
            coupon={coupon}
            onPayPalToken={onPayPalToken}
            plan={plan}
            productType={productType}
            purchaseTier={purchaseTier}
            setErrors={setErrors}
            setSubmitting={setSubmitting}
            submitting={submitting}
            webProPurchaseLocation={webProPurchaseLocation}
            validateCompliance={validateCompliance}
          />
        )}
      </div>
      <div>
        <SectionDivider>
          <FormattedMessage defaultMessage="Or enter payment details" />
        </SectionDivider>
      </div>
      <TokenizedCreditCardForm
        billingCountry={{
          onChange: onBillingCountryChange,
          value: billingCountry
        }}
        handleInputChange={(input, value: string | unknown) => {
          if (errors) {
            setErrors(undefined);
          }

          setClientValidationErrors(input, value as string | CardElementChangeEvent);
        }}
        isComplianceCheckboxEnabled={isComplianceCheckboxEnabled}
        isReiCheckout={isReiCheckout}
        isUpgrade={isUpgrade}
        purchaseTier={purchaseTier}
        validationErrors={validationErrors}
      />
      {errors && <ErrorText errors={errors} />}
      {submitting && <LoadingOverlay />}
    </>
  );
}
